"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import type { User } from "@supabase/supabase-js";
import { logger } from "@/utils/logger";
import {
  setGA4UserId,
  clearGA4UserId,
  trackAuthEvent,
} from "@/utils/analytics";
import { rolesManager } from "@/lib/auth";
import { isAnonymousVisitor } from "@/lib/auth/accountStatus";
import type { Roles } from "@/lib/auth/types";
import type { ServerAuthPayload, ServerAuthSession } from "@/types/auth";

interface AuthContextValue {
  user: User | null;
  session: ServerAuthSession | null;
  roles: Roles;
  loading: boolean;
  refreshSession: () => Promise<void>;
  /**
   * True for a Supabase anonymous session — a visitor designing before they
   * have an account (see /api/auth/anonymous).
   *
   * `session` is truthy for these, so every existing `if (!session)` check
   * reads them as signed in. That is deliberate and correct: they ARE
   * authenticated, they own their designs and their credit balance. Branch on
   * this flag only where the difference is what you actually mean — showing a
   * sign-up wall, or an account page that needs an email address.
   */
  // Optional until the create-page consumers and their shared auth fixtures
  // adopt the new identity state in the next stack layer. The provider always
  // supplies a concrete boolean.
  isAnonymous?: boolean;
  /** @deprecated OAuth is completed server-side through /auth/callback. */
  isOAuthFlow: boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);
export const AUTH_SESSION_REFRESH_TIMEOUT_MS = 15_000;

interface AuthProviderProps {
  children: ReactNode;
  initialSession?: ServerAuthSession | null;
  roleHydrationMode?: "eager" | "idle";
  verifyNullServerSnapshot?: boolean;
}

type IdleWindow = Window &
  typeof globalThis & {
    requestIdleCallback?: (
      callback: IdleRequestCallback,
      options?: IdleRequestOptions
    ) => number;
    cancelIdleCallback?: (handle: number) => void;
  };

function scheduleAuthIdleTask(task: () => void) {
  if (typeof window === "undefined") {
    return () => {};
  }

  const win = window as IdleWindow;
  if (win.requestIdleCallback) {
    const idleId = win.requestIdleCallback(task, { timeout: 1500 });
    return () => win.cancelIdleCallback?.(idleId);
  }

  const timeoutId = window.setTimeout(task, 250);
  return () => window.clearTimeout(timeoutId);
}

function hasServerAuthReturnMarker() {
  if (typeof window === "undefined") {
    return false;
  }

  try {
    return new URL(window.location.href).searchParams.get("_ssr_oauth") === "1";
  } catch {
    return false;
  }
}

function emptyRoles(): Roles {
  return {
    admin: false,
    manufacturer: false,
  };
}

/**
 * AuthProvider hydrates client UI from the server-side session endpoint.
 *
 * Auth tokens stay in HttpOnly Supabase SSR cookies. The browser never receives
 * access_token or refresh_token; client code uses this provider only for
 * display state and should call API routes with credentials included.
 */
export function AuthProvider({
  children,
  initialSession,
  roleHydrationMode = "eager",
  verifyNullServerSnapshot = false,
}: AuthProviderProps) {
  const hasServerAuthSnapshot = initialSession !== undefined;
  const needsInitialRoleHydration =
    !!initialSession?.user && roleHydrationMode === "eager";
  const [session, setSession] = useState<ServerAuthSession | null>(
    initialSession || null
  );
  const [user, setUser] = useState<User | null>(initialSession?.user ?? null);
  const [roles, setRoles] = useState<Roles>(emptyRoles);
  const [loading, setLoading] = useState(
    !hasServerAuthSnapshot || needsInitialRoleHydration
  );
  const activeUserIdRef = useRef<string | null>(
    initialSession?.user?.id ?? null
  );
  // Captured synchronously at first render: RouteAwareShell/PublicNavbar strip
  // the _ssr_oauth marker from the URL in their own effects, which run before
  // this provider's, so reading it any later misses it.
  const authReturnPendingRef = useRef(hasServerAuthReturnMarker());
  const initializedFromServerRef = useRef(false);
  const lastRolesUserIdRef = useRef<string | null>(null);
  const inFlightRolesFetchRef = useRef<{
    userId: string;
    promise: Promise<void>;
  } | null>(null);
  const inFlightSessionRefreshRef = useRef<Promise<void> | null>(null);
  const idleRolesCancelRef = useRef<(() => void) | null>(null);
  const idleSessionVerifyCancelRef = useRef<(() => void) | null>(null);

  const fetchRoles = useCallback(async (userId: string) => {
    if (lastRolesUserIdRef.current === userId) {
      return;
    }

    const existingInFlight = inFlightRolesFetchRef.current;
    if (existingInFlight) {
      await existingInFlight.promise;
      if (
        existingInFlight.userId === userId ||
        lastRolesUserIdRef.current === userId
      ) {
        return;
      }
    }

    const startTime = Date.now();
    const fetchPromise = (async () => {
      try {
        const fetchedRoles = await rolesManager.fetchRoles(true);
        if (activeUserIdRef.current !== userId) {
          return;
        }
        lastRolesUserIdRef.current = userId;
        setRoles(fetchedRoles);
        logger.info("AuthProvider roles fetched", {
          component: "AuthProvider",
          action: "fetch_roles_complete",
          metadata: {
            userId,
            durationMs: Date.now() - startTime,
          },
        });
      } catch (error) {
        if (activeUserIdRef.current === userId) {
          setRoles(emptyRoles());
        }
        logger.warn("AuthProvider role fetch failed", {
          component: "AuthProvider",
          action: "fetch_roles_error",
          metadata: {
            userId,
            error: error instanceof Error ? error.message : String(error),
          },
        });
      } finally {
        if (inFlightRolesFetchRef.current?.userId === userId) {
          inFlightRolesFetchRef.current = null;
        }
      }
    })();

    inFlightRolesFetchRef.current = {
      userId,
      promise: fetchPromise,
    };
    await fetchPromise;
  }, []);

  const scheduleIdleRoleFetch = useCallback(
    (userId: string) => {
      idleRolesCancelRef.current?.();
      idleRolesCancelRef.current = null;

      const run = () => {
        idleRolesCancelRef.current = null;
        fetchRoles(userId).catch(error => {
          logger.warn("AuthProvider idle role fetch failed", {
            component: "AuthProvider",
            action: "idle_fetch_roles_error",
            metadata: {
              userId,
              error: error instanceof Error ? error.message : String(error),
            },
          });
        });
      };

      if (typeof window === "undefined") {
        return;
      }

      const win = window as IdleWindow;
      if (win.requestIdleCallback) {
        const idleId = win.requestIdleCallback(run, { timeout: 2000 });
        idleRolesCancelRef.current = () => win.cancelIdleCallback?.(idleId);
        return;
      }

      const timeoutId = window.setTimeout(run, 500);
      idleRolesCancelRef.current = () => window.clearTimeout(timeoutId);
    },
    [fetchRoles]
  );

  // Fire the GA4 login/sign_up event once per server-auth return. Analytics
  // must never affect auth state, so failures are swallowed.
  const maybeTrackAuthReturn = useCallback((authedUser: User) => {
    if (!authReturnPendingRef.current) {
      return;
    }
    authReturnPendingRef.current = false;
    try {
      const createdAtMs = Date.parse(authedUser.created_at ?? "");
      const isNewUser =
        Number.isFinite(createdAtMs) && Date.now() - createdAtMs < 5 * 60_000;
      trackAuthEvent(
        isNewUser ? "signup" : "login",
        authedUser.app_metadata?.provider || "email"
      );
    } catch {
      // Never let analytics interfere with auth hydration.
    }
  }, []);

  const applyAuthPayload = useCallback(
    async (payload: ServerAuthPayload) => {
      idleRolesCancelRef.current?.();
      idleRolesCancelRef.current = null;

      const nextSession = payload.session;
      const nextUser = payload.user || nextSession?.user || null;

      const previousUserId = activeUserIdRef.current;
      setSession(nextSession);
      setUser(nextUser);
      activeUserIdRef.current = nextUser?.id ?? null;

      if (!nextUser) {
        setRoles(emptyRoles());
        lastRolesUserIdRef.current = null;
        inFlightRolesFetchRef.current = null;
        rolesManager.clearRoles();
        clearGA4UserId();
        if (previousUserId) {
          try {
            trackAuthEvent("logout");
          } catch {
            // Never let analytics interfere with auth hydration.
          }
        }
        return;
      }

      setGA4UserId(nextUser.id);
      maybeTrackAuthReturn(nextUser);
      if (payload.roles) {
        setRoles(payload.roles);
        lastRolesUserIdRef.current = nextUser.id;
      } else if (roleHydrationMode === "eager") {
        await fetchRoles(nextUser.id);
      } else {
        scheduleIdleRoleFetch(nextUser.id);
      }
    },
    [fetchRoles, maybeTrackAuthReturn, roleHydrationMode, scheduleIdleRoleFetch]
  );

  const refreshSession = useCallback(async () => {
    if (inFlightSessionRefreshRef.current) {
      return inFlightSessionRefreshRef.current;
    }

    // An explicit refresh often follows a cookie change (anonymous creation,
    // signup, or sign-in). A recent completed request may describe the old
    // identity, so only overlapping requests can safely be deduplicated.
    const refreshPromise = (async () => {
      const startTime = Date.now();
      const controller = new AbortController();
      const timeoutId = window.setTimeout(
        () => controller.abort(),
        AUTH_SESSION_REFRESH_TIMEOUT_MS
      );
      try {
        const response = await fetch("/api/auth/session", {
          method: "GET",
          credentials: "include",
          cache: "no-store",
          signal: controller.signal,
        });

        if (!response.ok) {
          logger.warn("AuthProvider session hydration failed", {
            component: "AuthProvider",
            status: response.status,
          });
          if (response.status === 401) {
            await applyAuthPayload({ session: null, user: null });
          }
          return;
        }

        const payload = (await response.json()) as ServerAuthPayload;
        await applyAuthPayload(payload);
        logger.info("AuthProvider session hydrated", {
          component: "AuthProvider",
          action: "hydrate_session",
          metadata: {
            hasUser: !!payload.user || !!payload.session?.user,
            durationMs: Date.now() - startTime,
          },
        });
      } catch (error) {
        logger.warn("AuthProvider session hydration error", {
          component: "AuthProvider",
          action: "hydrate_session_error",
          metadata: {
            error: error instanceof Error ? error.message : String(error),
          },
        });
      } finally {
        window.clearTimeout(timeoutId);
      }
    })();

    inFlightSessionRefreshRef.current = refreshPromise;
    try {
      await refreshPromise;
    } finally {
      if (inFlightSessionRefreshRef.current === refreshPromise) {
        inFlightSessionRefreshRef.current = null;
      }
    }
  }, [applyAuthPayload]);

  useEffect(() => {
    let cancelled = false;

    const hydrate = async () => {
      if (hasServerAuthSnapshot && !initializedFromServerRef.current) {
        initializedFromServerRef.current = true;
        if (!initialSession) {
          if (hasServerAuthReturnMarker()) {
            setLoading(true);
            await refreshSession();
            if (!cancelled) {
              setLoading(false);
            }
            return;
          }

          setSession(null);
          setUser(null);
          setRoles(emptyRoles());
          activeUserIdRef.current = null;
          lastRolesUserIdRef.current = null;
          inFlightRolesFetchRef.current = null;
          inFlightSessionRefreshRef.current = null;
          rolesManager.clearRoles();
          clearGA4UserId();
          setLoading(false);

          if (verifyNullServerSnapshot) {
            idleSessionVerifyCancelRef.current = scheduleAuthIdleTask(() => {
              refreshSession().catch(error => {
                logger.warn(
                  "AuthProvider idle null snapshot verification failed",
                  {
                    component: "AuthProvider",
                    action: "idle_verify_null_snapshot_error",
                    metadata: {
                      error:
                        error instanceof Error ? error.message : String(error),
                    },
                  }
                );
              });
            });
          }
          return;
        }

        setGA4UserId(initialSession.user.id);
        maybeTrackAuthReturn(initialSession.user);
        if (roleHydrationMode === "eager") {
          await fetchRoles(initialSession.user.id);
          if (cancelled) {
            return;
          }
        } else {
          scheduleIdleRoleFetch(initialSession.user.id);
        }
        setLoading(false);
        idleSessionVerifyCancelRef.current = scheduleAuthIdleTask(() => {
          refreshSession().catch(error => {
            logger.warn("AuthProvider idle session verification failed", {
              component: "AuthProvider",
              action: "idle_verify_session_error",
              metadata: {
                error: error instanceof Error ? error.message : String(error),
              },
            });
          });
        });
        return;
      }

      setLoading(true);
      await refreshSession();
      if (!cancelled) {
        setLoading(false);
      }
    };

    hydrate();

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refreshSession();
      }
    };

    window.addEventListener("focus", refreshSession);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      cancelled = true;
      idleRolesCancelRef.current?.();
      idleRolesCancelRef.current = null;
      idleSessionVerifyCancelRef.current?.();
      idleSessionVerifyCancelRef.current = null;
      window.removeEventListener("focus", refreshSession);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [
    hasServerAuthSnapshot,
    fetchRoles,
    initialSession,
    maybeTrackAuthReturn,
    refreshSession,
    roleHydrationMode,
    scheduleIdleRoleFetch,
    verifyNullServerSnapshot,
  ]);

  const value: AuthContextValue = useMemo(
    () => ({
      user,
      session,
      roles,
      loading,
      refreshSession,
      // NOT `user.is_anonymous` — with email confirmation on, that claim stays
      // true after a customer has started an upgrade. Payment uses the stricter
      // hasVerifiedAccount boundary. See isAnonymousVisitor.
      isAnonymous: isAnonymousVisitor(user),
      isOAuthFlow: false,
    }),
    [user, session, roles, loading, refreshSession]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
